import { MinusIcon, PlusIcon } from 'lucide-react';

import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { BuyRateRule } from 'types/agents/QuickQuoteAgent';

import {
  formatBuyRateBandDollars,
  formatDollarsNoCents,
  formatOrdinal,
} from '../utils';

/** Human-readable line: lower / higher / middle of the quote band for a given percentile. */
export function getPercentileRangeSelectionLabel(percentile: number): string {
  const ord = formatOrdinal(percentile);
  if (percentile < 50) {
    return `Selected the lower end of the range (${ord} percentile).`;
  }
  if (percentile > 50) {
    return `Selected the higher end of the range (${ord} percentile).`;
  }
  return `Selected the middle of the range (${ord} percentile).`;
}

/**
 * Lowercase fragment for inline prose (after a comma), aligned with
 * {@link getPercentileRangeSelectionLabel} / the buy rate tooltip.
 */
export function getPercentileRangeSelectionInline(percentile: number): string {
  const ord = formatOrdinal(percentile);
  if (percentile < 50) {
    return `selected the lower end of the quote range (${ord} percentile)`;
  }
  if (percentile > 50) {
    return `selected the higher end of the quote range (${ord} percentile)`;
  }
  return `selected the middle of the quote range (${ord} percentile)`;
}

/** Signed dollar change for display (e.g. −$59, +$12). */
function formatSignedBuyRateDelta(
  rateAfter: number,
  rateBefore: number
): string {
  const delta = rateAfter - rateBefore;
  const rounded = Math.round(Math.abs(delta));
  const dollars = `$${rounded}`;
  if (delta > 0) {
    return `+${dollars}`;
  }
  if (delta < 0) {
    return `−${dollars}`;
  }
  return '$0';
}

interface BuyRateRuleRowProps {
  rule: BuyRateRule;
}

export const BuyRateRuleRow = ({ rule }: BuyRateRuleRowProps) => {
  const isPositive = rule.rateAfter > rule.rateBefore;
  const isZero = rule.rateAfter === rule.rateBefore;

  let adjustmentDisplay: string;
  if (rule.adjustmentType === 'rangePercentile') {
    // Prefer `BuyRateRangePercentileRuleBlock` in tooltips; keep fallback copy.
    adjustmentDisplay = `${formatOrdinal(rule.adjustmentValue)} percentile`;
  } else if (rule.adjustmentType === 'percentage') {
    const sign = isPositive ? '+' : isZero ? '' : '-';
    adjustmentDisplay = `${sign}${Math.abs(rule.adjustmentValue)}%`;
  } else {
    const absValue = Math.abs(rule.adjustmentValue);
    const dollarFormatted = formatDollarsNoCents(absValue);
    const sign = isPositive ? '+' : isZero ? '' : '-';
    adjustmentDisplay = `${sign}${dollarFormatted}`;
  }

  const Icon = isPositive ? PlusIcon : MinusIcon;

  return (
    <Flex align='center' justify='between' className='w-full'>
      <Flex align='center' gap='xs'>
        <Icon
          className={`w-3 h-3 ${isPositive ? 'text-success-main' : isZero ? 'text-neutral-400' : 'text-error-main'}`}
        />
        <Typography variant='body-xxs' textColor='lessMuted'>
          {rule.ruleName}
        </Typography>
      </Flex>
      <Typography
        variant='body-xxs'
        weight='medium'
        className={
          isPositive
            ? 'text-success-main'
            : isZero
              ? 'text-neutral-500'
              : 'text-error-main'
        }
      >
        {adjustmentDisplay}
      </Typography>
    </Flex>
  );
};

type BuyRateRangePercentileRuleBlockProps = {
  rule: BuyRateRule;
  rangeLow?: number;
  rangeHigh?: number;
};

/**
 * Rich layout for DAT / range-percentile rules: band, selection copy, and dollar delta on its own row.
 */
export function BuyRateRangePercentileRuleBlock({
  rule,
  rangeLow,
  rangeHigh,
}: BuyRateRangePercentileRuleBlockProps) {
  const delta = rule.rateAfter - rule.rateBefore;
  // Match formatSignedBuyRateDelta: dollar amount is rounded, so "zero" styling must use the same threshold.
  const roundedAbsDollars = Math.round(Math.abs(delta));
  const isZero = roundedAbsDollars === 0;
  const isPositive = !isZero && delta > 0;

  return (
    <Flex
      direction='col'
      gap='sm'
      className='w-full rounded-md border border-neutral-200 bg-neutral-50 p-2.5'
    >
      <Typography
        variant='body-xxs'
        weight='medium'
        className='leading-snug text-neutral-900'
      >
        {rule.ruleName}
      </Typography>

      {rangeLow != null && rangeHigh != null && (
        <Flex direction='col' gap='xs' className='w-full'>
          <Flex align='center' justify='between' className='w-full gap-2'>
            <Typography variant='body-xxs' textColor='lessMuted'>
              Range: Low-High
            </Typography>
            <Typography
              variant='body-xxs'
              weight='semibold'
              className='shrink-0 tabular-nums text-neutral-900'
            >
              {formatBuyRateBandDollars(rangeLow)}-
              {formatBuyRateBandDollars(rangeHigh)}
            </Typography>
          </Flex>
        </Flex>
      )}

      <Typography
        variant='body-xxs'
        textColor='lessMuted'
        className='leading-snug'
      >
        {getPercentileRangeSelectionLabel(rule.adjustmentValue)}
      </Typography>

      <Flex
        direction='col'
        gap='xs'
        className='w-full border-t border-neutral-200/90 pt-2'
      >
        <Typography variant='body-xxs' textColor='lessMuted'>
          Adjustment to buy rate
        </Typography>
        <Typography
          variant='body-xxs'
          weight='semibold'
          className={`tabular-nums ${
            isPositive
              ? 'text-success-main'
              : isZero
                ? 'text-neutral-500'
                : 'text-error-main'
          }`}
        >
          {formatSignedBuyRateDelta(rule.rateAfter, rule.rateBefore)}
        </Typography>
      </Flex>
    </Flex>
  );
}

type BuyRateQuoteRangeRowProps = {
  low?: number;
  high?: number;
  targetBuyRate?: number;
  targetLabel?: string;
};

/** Provider quote band; render only when both values are present. */
export function BuyRateQuoteRangeRow({
  low,
  high,
  targetBuyRate,
  targetLabel = 'Target Buy',
}: BuyRateQuoteRangeRowProps) {
  if (low == null || high == null) {
    return null;
  }

  return (
    <Flex direction='col' gap='xs' className='w-full'>
      <Flex align='center' justify='between' className='w-full gap-2'>
        <Typography variant='body-xxs' textColor='lessMuted'>
          Range: Low-High
        </Typography>
        <Typography
          variant='body-xxs'
          weight='semibold'
          className='shrink-0 tabular-nums text-neutral-900'
        >
          {formatBuyRateBandDollars(low)}-{formatBuyRateBandDollars(high)}
        </Typography>
      </Flex>
      {targetBuyRate != null && (
        <Flex align='center' justify='between' className='w-full gap-2'>
          <Typography variant='body-xxs' textColor='lessMuted'>
            {targetLabel}
          </Typography>
          <Typography
            variant='body-xxs'
            weight='semibold'
            className='shrink-0 tabular-nums text-neutral-900'
          >
            {formatDollarsNoCents(targetBuyRate)}
          </Typography>
        </Flex>
      )}
    </Flex>
  );
}
